import React, { useEffect, useState, useMemo } from 'react';
import { FileText, Download, RefreshCw, ChevronRight, Search, X } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

/**
 * Logs tab for EntryDeviceDetail.
 *
 * Lists log captures (populated by the RETRIEVE_LOGS Quick Command) and
 * opens a detail viewer when a row is clicked. Shows monospace log content
 * with a search box and a .txt download button.
 *
 * Data source:
 *   GET /api/entry/devices/:deviceId/logs        -> list metadata only
 *   GET /api/entry/devices/:deviceId/logs/:logId -> full log content
 */
export default function LogsTab({ deviceId, deviceName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);   // metadata of the log being viewed
  const [content, setContent] = useState(null);     // { logs, logLevel, ... } full
  const [contentLoading, setContentLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithTenant(`/api/entry/devices/${deviceId}/logs`);
      const data = await res.json();
      if (data.success) {
        setLogs(data.logs || []);
      } else {
        setError(data.error || 'Failed to load logs');
      }
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const openLog = async (log) => {
    setSelected(log);
    setContent(null);
    setContentLoading(true);
    setFilter('');
    try {
      const res = await fetchWithTenant(`/api/entry/devices/${deviceId}/logs/${log.id}`);
      const data = await res.json();
      if (data.success) {
        setContent(data.log);
      } else {
        setContent({ logs: `Failed to load: ${data.error || 'unknown error'}`, logLevel: '' });
      }
    } catch (e) {
      setContent({ logs: `Network error: ${e.message}`, logLevel: '' });
    } finally {
      setContentLoading(false);
    }
  };

  const closeViewer = () => {
    setSelected(null);
    setContent(null);
    setFilter('');
  };

  const downloadLog = () => {
    if (!content || !selected) return;
    const safeName = (deviceName || 'tablet').replace(/[^a-zA-Z0-9_-]/g, '_');
    const when = new Date(selected.createdAt).toISOString().replace(/[:.]/g, '-');
    const filename = `${safeName}_${when}.log.txt`;
    const blob = new Blob([content.logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // When filtering, show only lines that match (case-insensitive)
  const filteredLogs = useMemo(() => {
    if (!content?.logs) return '';
    if (!filter.trim()) return content.logs;
    const needle = filter.toLowerCase();
    const lines = content.logs.split('\n');
    return lines.filter((l) => l.toLowerCase().includes(needle)).join('\n');
  }, [content, filter]);

  return (
    <div className="entry-logs-tab">
      <div className="entry-logs-header">
        <div>
          <h3>
            <FileText size={16} /> Log Captures
          </h3>
          <p className="entry-logs-subtitle">
            Logcat content uploaded via the Retrieve Logs quick command.
          </p>
        </div>
        <button
          type="button"
          className="entry-btn-secondary"
          onClick={loadList}
          disabled={loading}
          title="Refresh list"
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />{' '}
          Refresh
        </button>
      </div>

      {error && <div className="entry-logs-error">{error}</div>}

      {!loading && logs.length === 0 && !error && (
        <div className="entry-logs-empty">
          <p>No log captures yet.</p>
          <p className="entry-logs-empty-hint">
            Click <strong>Retrieve Logs</strong> on the Overview tab to pull
            logcat from the tablet.
          </p>
        </div>
      )}

      {logs.length > 0 && (
        <table className="entry-logs-table">
          <thead>
            <tr>
              <th>Captured at</th>
              <th>Size</th>
              <th>Priority</th>
              <th>App version</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr
                key={log.id}
                className="entry-logs-row"
                onClick={() => openLog(log)}
              >
                <td>{new Date(log.createdAt).toLocaleString()}</td>
                <td>{formatBytes(log.backupSizeBytes)}</td>
                <td>{priorityLabel(log.logLevel)}</td>
                <td>{log.appVersion || '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <ChevronRight size={14} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="entry-logs-modal-backdrop" onClick={closeViewer}>
          <div
            className="entry-logs-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="entry-logs-modal-header">
              <div>
                <h4>
                  <FileText size={14} /> Log capture
                </h4>
                <p className="entry-logs-modal-subtitle">
                  {new Date(selected.createdAt).toLocaleString()} ·{' '}
                  {formatBytes(selected.backupSizeBytes)} ·{' '}
                  {priorityLabel(selected.logLevel)} · v{selected.appVersion || '?'}
                </p>
              </div>
              <div className="entry-logs-modal-actions">
                <button
                  type="button"
                  className="entry-btn-secondary"
                  onClick={downloadLog}
                  disabled={!content}
                  title="Download as .txt"
                >
                  <Download size={14} /> Download
                </button>
                <button
                  type="button"
                  className="entry-btn-secondary"
                  onClick={closeViewer}
                  title="Close"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="entry-logs-filter">
              <Search size={12} />
              <input
                type="text"
                placeholder="Filter lines (case-insensitive)…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter && (
                <button
                  type="button"
                  className="entry-logs-filter-clear"
                  onClick={() => setFilter('')}
                  title="Clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="entry-logs-modal-body">
              {contentLoading && <div className="entry-logs-loading">Loading {formatBytes(selected.backupSizeBytes)}…</div>}
              {!contentLoading && content && (
                <pre className="entry-logs-pre">{filteredLogs || '(no matching lines)'}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function priorityLabel(p) {
  switch ((p || '').toUpperCase()) {
    case 'V': return 'Verbose';
    case 'D': return 'Debug';
    case 'I': return 'Info';
    case 'W': return 'Warn';
    case 'E': return 'Error';
    case 'F': return 'Fatal';
    default:  return p || 'Info';
  }
}
